import {
  type DatabaseView,
  DEFAULT_DATABASE_VIEW_SETTINGS,
} from '@databases/types/database/view';
import { proxy, snapshot, subscribe } from 'valtio';
import { devtools } from 'valtio/utils';

export const viewState = proxy<{ view: DatabaseView }>({
  view: {
    id: '',
    name: '',
    settings: DEFAULT_DATABASE_VIEW_SETTINGS,
  },
});

export const setView = (view: DatabaseView) => {
  viewState.view = view;
};

let cachedView: DatabaseView | undefined;
let unsubscribe: (() => void) | undefined;

/**
 * Returns a cached snapshot of the current database view settings.
 *
 * **Why this exists:**
 * `snapshot()` creates a new object reference every time it's called, even when data hasn't changed.
 * This breaks Redux selector memoization (re-reselect), causing unnecessary recalculations.
 * This function caches the snapshot and updates it via subscription, maintaining referential equality.
 *
 * **Implementation note:**
 * Uses lazy initialization to avoid test failures when valtio is mocked at module load time.
 */
export const getCachedViewSnapshot = (): DatabaseView => {
  if (!unsubscribe && subscribe) {
    unsubscribe = subscribe(viewState.view, () => {
      cachedView = snapshot(viewState.view) as DatabaseView;
    });
  }
  if (!cachedView) {
    cachedView = snapshot(viewState.view) as DatabaseView;
  }
  return cachedView;
};

if (process.env.NODE_ENV === 'development') {
  devtools(viewState, 'Valtio View State');
}
